/**
 * MotionBuffer — Ring buffer anti-lag para eventos de SpaceMouse.
 *
 * Desacopla la captura (requestAnimationFrame ~60Hz) del envío por socket,
 * evitando que backpressure del socket bloquee el render loop.
 *
 * - push() es O(1), nunca bloquea.
 * - Un drain loop independiente vacía el buffer a alta frecuencia.
 * - Si hay congestión (más frames acumulados que maxBatch), descarta los
 *   más antiguos y solo envía los más recientes.
 */

export interface MotionBufferOptions {
  /** Intervalo del drain loop en ms (default: 16 ≈ 60Hz) */
  drainInterval?: number;
  /** Máximo de frames a enviar por ciclo de drain. Si hay más, descarta los antiguos. */
  maxBatch?: number;
  /** Tamaño del ring buffer (default: 16) */
  bufferSize?: number;
}

export class MotionBuffer<T> {
  private buffer: (T | null)[];
  private writeIndex = 0;
  private readIndex = 0;
  private count = 0;
  private readonly size: number;
  private readonly maxBatch: number;
  private drainTimer: ReturnType<typeof setInterval> | null = null;
  private onFlush: ((data: T) => void) | null = null;

  constructor(options: MotionBufferOptions = {}) {
    const { drainInterval = 16, maxBatch = 3, bufferSize = 16 } = options;
    this.size = bufferSize;
    this.maxBatch = maxBatch;
    this.buffer = new Array(this.size).fill(null);

    this.drainTimer = setInterval(() => this.drain(), drainInterval);
  }

  /**
   * Registrar callback de flush. Se invoca una vez por cada frame enviado.
   */
  setFlushCallback(callback: (data: T) => void): void {
    this.onFlush = callback;
  }

  /**
   * Empujar un frame al buffer. O(1), nunca bloquea.
   * Si el buffer está lleno, sobrescribe el frame más antiguo.
   */
  push(data: T): void {
    this.buffer[this.writeIndex] = data;
    this.writeIndex = (this.writeIndex + 1) % this.size;

    if (this.count < this.size) {
      this.count++;
    } else {
      // Buffer lleno: avanzar readIndex (descartamos el más antiguo)
      this.readIndex = (this.readIndex + 1) % this.size;
    }
  }

  /**
   * Drain loop: lee frames pendientes y los envía vía callback.
   * Si hay más de maxBatch frames acumulados, descarta los antiguos
   * y solo envía los últimos maxBatch.
   */
  private drain(): void {
    if (this.count === 0 || !this.onFlush) return;

    // Si hay congestión, saltar a los frames más recientes
    if (this.count > this.maxBatch) {
      const skip = this.count - this.maxBatch;
      this.readIndex = (this.readIndex + skip) % this.size;
      this.count = this.maxBatch;
    }

    // Enviar los frames pendientes
    const toSend = this.count;
    for (let i = 0; i < toSend; i++) {
      const data = this.buffer[this.readIndex];
      if (data !== null) {
        this.onFlush(data);
      }
      this.buffer[this.readIndex] = null;
      this.readIndex = (this.readIndex + 1) % this.size;
      this.count--;
    }
  }

  /**
   * Destruir el buffer y detener el drain loop.
   */
  destroy(): void {
    if (this.drainTimer !== null) {
      clearInterval(this.drainTimer);
      this.drainTimer = null;
    }
    this.onFlush = null;
    this.count = 0;
    this.readIndex = 0;
    this.writeIndex = 0;
    this.buffer.fill(null);
  }
}
